"use client";

import { c as _c } from "react/compiler-runtime";
import Image, { type ImageProps } from "next/image";
import { useMediaVariants } from "@/components/ui/MediaVariantsProvider";
import { pickMediaVariant } from "@/utilities/media/mediaVariants";
const DEFAULT_SIZES = "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px";

/**
 * FillImage - A wrapper around Next.js Image that defaults to fill={true}.
 *
 * This is needed because the fansunited-sports-ui library's ContentCard component
 * doesn't pass fill={true} to custom ImageComponents, which Next.js Image requires
 * when width/height are not provided.
 *
 * Inside a `MediaVariantsProvider` that knows the `src`, the image gets a `srcset` built
 * from Payload's pre-generated renditions (custom loader, no Next.js optimizer) and, for
 * priority scopes, eager loading with `fetchpriority="high"`. Otherwise it renders
 * unoptimized, as a plain `src`.
 *
 * @example
 * import { FillImage } from "@/components/ui/FillImage";
 * <ContentCard ImageComponent={FillImage} ... />
 */
export function FillImage(props) {
  const $ = _c(12);
  const src = typeof props.src === "string" ? props.src : "";
  const scope = useMediaVariants(src);
  if (scope) {
    const isPriority = scope.priority || Boolean(props.priority);
    const t0 = props.sizes ?? scope.sizes ?? DEFAULT_SIZES;
    let t1;
    if ($[0] !== scope.variants || $[1] !== src) {
      t1 = t2 => {
        const {
          width
        } = t2;
        return pickMediaVariant(scope.variants, width)?.url ?? src;
      };
      $[0] = scope.variants;
      $[1] = src;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const t2 = isPriority ? "high" : undefined;
    let t3;
    if ($[3] !== isPriority || $[4] !== props || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
      t3 = <Image {...props} fill={true} sizes={t0} loader={t1} priority={isPriority} fetchPriority={t2} />;
      $[3] = isPriority;
      $[4] = props;
      $[5] = t0;
      $[6] = t1;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  const sizes = props.sizes || DEFAULT_SIZES;
  let t0;
  if ($[9] !== props || $[10] !== sizes) {
    t0 = <Image {...props} fill={true} sizes={sizes} unoptimized={true} />;
    $[9] = props;
    $[10] = sizes;
    $[11] = t0;
  } else {
    t0 = $[11];
  }
  return t0;
}